import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Button, message, Select, Row, Col, Alert } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { getRoles } from '../services/roleService';

const ROLE_CONFLICTS = [
  { admin: 'COMPLAINTS_ADMIN', view: 'COMPLAINTS_VIEW', label: 'Complaints' },
  { admin: 'INCIDENTS_ADMIN',  view: 'INCIDENTS_VIEW',  label: 'Incidents'  },
];

function getConflicts(selectedRoles) {
  return ROLE_CONFLICTS.filter(
    ({ admin, view }) => selectedRoles.includes(admin) && selectedRoles.includes(view)
  );
}

const { Option } = Select;

export default function UserModal({
  open,
  onCancel,
  onSave,
  mode,
  selectedUser,
  saving,
  form
}) {
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [conflicts, setConflicts] = useState([]);

  useEffect(() => {
    setLoadingRoles(true);
    getRoles()
      .then(data => setRoles(data))
      .catch(err => {
        console.error("Failed to fetch roles", err);
        message.error("Could not load roles");
      })
      .finally(() => setLoadingRoles(false));
  }, []);

  useEffect(() => {
    if (!form) return;

    if (open && mode === 'edit' && selectedUser) {
      const selectedRoles = selectedUser.roles?.map(role => role.name) || [];
      form.setFieldsValue({ email: selectedUser.email, roles: selectedRoles });
      setConflicts(getConflicts(selectedRoles));
    } else if (open) {
      form.resetFields();
      setConflicts([]);
    }
  }, [open, mode, selectedUser, form]);

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleFinish = (values) => {
    const { confirmPassword, ...userData } = values;
    if (mode === 'edit') userData.username = selectedUser.username;
    onSave(userData, form);
  };

  return (
    <Modal
      title={mode === 'create' ? 'Create New User' : 'Edit User'}
      open={open}
      onCancel={handleCancel}
      footer={null}
      maskClosable={false}
    >
      <Form
        form={form}
        layout="vertical"
        name="user-form"
        onFinish={handleFinish}
        autoComplete="off"
      >
        {mode === 'create' ? (
          <Form.Item
            label="Username"
            name="username"
            rules={[
              { required: true, message: 'Username is required' },
              { whitespace: true, message: 'Username cannot be empty' }
            ]}
          >
            <Input placeholder="Enter username" autoComplete="off" />
          </Form.Item>
        ) : (
          <Form.Item label="Username">
            <Input value={selectedUser?.username} disabled />
          </Form.Item>
        )}

        <Form.Item
          label="Email"
          name="email"
          rules={[
            { type: 'email', message: 'Invalid email' },
            { required: true, message: 'Email is required' },
            { whitespace: true, message: 'Email cannot be empty' }
          ]}
        >
          <Input placeholder="example@domain.com" autoComplete="off" />
        </Form.Item>

        <Form.Item
          label="Password"
          name="password"
          rules={
            mode === 'create'
              ? [
                  { required: true, min: 6, message: 'Min 6 characters' },
                  { whitespace: true, message: 'Password cannot be empty' }
                ]
              : []
          }
          hasFeedback
        >
          <Input.Password
            placeholder={
              mode === 'edit'
                ? 'Leave blank to keep current password'
                : 'Enter password'
            }
            autoComplete="new-password"
          />
        </Form.Item>

        {mode === 'create' && (
          <Form.Item
            label="Confirm Password"
            name="confirmPassword"
            dependencies={['password']}
            hasFeedback
            rules={[
              { required: true, message: 'Please confirm your password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirm password" autoComplete="new-password" />
          </Form.Item>
        )}

        <Form.Item
          label="Roles"
          name="roles"
          rules={[{ required: true, message: 'Select at least one role' }]}
        >
          <Select
            mode="multiple"
            placeholder="Select roles"
            loading={loadingRoles}
            allowClear
            getPopupContainer={trigger => trigger.parentNode}
            onChange={selected => setConflicts(getConflicts(selected))}
          >
            {roles.map(role => (
              <Option key={role.id} value={role.name}>
                {role.description}
              </Option>
            ))}
          </Select>
        </Form.Item>
        {conflicts.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Conflicting roles selected"
            description={
              <>
                {conflicts.map(({ label, view }, i) => {
                  const viewDesc = roles.find(r => r.name === view)?.description ?? view;
                  return (
                    <div key={label} style={i > 0 ? { marginTop: 6 } : undefined}>
                      {label}: admin and view roles are both selected — <strong>{viewDesc}</strong> will be applied.
                    </div>
                  );
                })}
              </>
            }
            icon={<WarningOutlined style={{ fontSize: 13, marginTop: 2 }} />}
          />
        )}

        <Form.Item>
          <Row gutter={8} justify="end">
            <Col>
              <Button onClick={handleCancel}>Cancel</Button>
            </Col>
            <Col>
              <Button
                type="primary"
                htmlType="submit"
                loading={saving}
                style={{
                  backgroundColor: '#0075C9',
                  borderRadius: '8px'
                }}
              >
                {mode === 'create' ? 'Create' : 'Save'}
              </Button>
            </Col>
          </Row>
        </Form.Item>
      </Form>
    </Modal>
  );
}
